import useSimStatuses from 'api/sims/useSimStatuses';
import isArray from 'lodash/isArray';
import { useMemo } from 'react';
import useSimInventoryStoredFilters from 'state/SimInventory/useSimInventoryStoredFilters';
import useOrganization from 'state/contexts/Organization/useOrganization';
import type {
  EnumsSimNetworkState,
  StructsDateRange,
  StructsSimRequestProperties,
} from 'types/__generated__/SimInventoryApi';
import type { DateFilter, SimInventorySegmentName } from 'types/common';
import inputDateToDateTime from 'utils/dates/inputDateToDateTime';
import hasUserFiltersForSegment from 'utils/filters/hasUserFiltersForSegment';
import isValidDateFilter from 'utils/filters/isValidDateFilter';

const dateFilterToApiFilter = (dateFilter?: DateFilter): StructsDateRange | undefined => {
  if (!isValidDateFilter(dateFilter)) {
    return undefined;
  }

  const newDateFilter = {
    startTime:
      inputDateToDateTime(dateFilter.start)?.toUTC().startOf('day')?.toISO({}) ?? undefined,
    endTime: inputDateToDateTime(dateFilter.end)?.toUTC()?.endOf('day')?.toISO({}) ?? undefined,
  };

  if (newDateFilter.startTime && newDateFilter.endTime) {
    return newDateFilter as unknown as StructsDateRange;
  }

  return undefined;
};

/**
 * Translation layer between Zustand state and SIM Inventory for filters
 */

interface UseSimInventoryFiltersProps {
  segmentName: SimInventorySegmentName;
}

const useSimInventoryFilters = ({ segmentName }: UseSimInventoryFiltersProps) => {
  const storedFilters = useSimInventoryStoredFilters();

  const { getNetworkStatusFromFriendly, networkStatusGroups, isLoading, error } = useSimStatuses();

  const organizationId = useOrganization();

  // Transform the filters
  // TODO: should this behavior go back in the table? This is an antipattern and using the hook transforms the output at all.
  const copiedStoredFilters = useMemo(() => {
    const newFilters = structuredClone(storedFilters) ?? {};

    if (segmentName !== 'all') {
      newFilters.organizationId = organizationId;
    }

    return newFilters;
  }, [organizationId, segmentName, storedFilters]);

  const { filters, hasFilters } = useMemo(() => {
    const simInventoryFilters: StructsSimRequestProperties = {};
    let newHasFilters = false;

    if (copiedStoredFilters?.organizationId) {
      simInventoryFilters.organizationId = copiedStoredFilters.organizationId;
      newHasFilters = true;
    }

    if (copiedStoredFilters?.deviceName) {
      simInventoryFilters.deviceName = copiedStoredFilters.deviceName;
      newHasFilters = true;
    }

    if (copiedStoredFilters?.iccid) {
      simInventoryFilters.iccidFilterProperty = {
        iccid: copiedStoredFilters.iccid.value,
        iccidCondition: copiedStoredFilters.iccid.condition,
      };
      newHasFilters = true;
    }

    const newLastActiveDateFilter = dateFilterToApiFilter(copiedStoredFilters?.lastActive);
    if (newLastActiveDateFilter) {
      simInventoryFilters.lastActiveDate = newLastActiveDateFilter;
      newHasFilters = true;
    }

    const newActivationDateFilter = dateFilterToApiFilter(copiedStoredFilters?.activationDate);
    if (newActivationDateFilter) {
      simInventoryFilters.activationTime = newActivationDateFilter;
      newHasFilters = true;
    }

    const newTargetActivationDateFilter = dateFilterToApiFilter(
      copiedStoredFilters?.targetActivationDate
    );
    if (newTargetActivationDateFilter) {
      simInventoryFilters.expectedActivationTime = newTargetActivationDateFilter;
      newHasFilters = true;
    }

    if (copiedStoredFilters?.imei) {
      simInventoryFilters.imeiFilterProperty = {
        imei: copiedStoredFilters.imei.value,
        imeiCondition: copiedStoredFilters.imei.condition,
      };
      newHasFilters = true;
    }

    if (copiedStoredFilters?.tags && copiedStoredFilters.tags.length > 0) {
      simInventoryFilters.tagIds = copiedStoredFilters.tags;
      newHasFilters = true;
    }

    if (copiedStoredFilters?.noTags) {
      simInventoryFilters.filterNoTags = copiedStoredFilters.noTags;
      newHasFilters = true;
    }

    if (copiedStoredFilters?.plans && copiedStoredFilters.plans.length > 0) {
      simInventoryFilters.planID = copiedStoredFilters.plans;
      newHasFilters = true;
    }

    if (
      copiedStoredFilters?.simStatuses &&
      isArray(copiedStoredFilters?.simStatuses) &&
      copiedStoredFilters.simStatuses.length > 0
    ) {
      const statuses = copiedStoredFilters.simStatuses?.reduce((accumulator, status) => {
        getNetworkStatusFromFriendly(status).forEach((internalStatus) => {
          accumulator.add(internalStatus);
        });
        return accumulator;
      }, new Set<EnumsSimNetworkState>());

      simInventoryFilters.networkStatus = [...(statuses?.values() ?? [])].sort((a, b) =>
        a.localeCompare(b)
      );
      newHasFilters = true;
    } else if (segmentName !== 'all') {
      if (segmentName === 'active') {
        simInventoryFilters.networkStatus = networkStatusGroups?.active;
      } else if (segmentName === 'preflight') {
        simInventoryFilters.networkStatus = networkStatusGroups?.predeployment;
      } else {
        simInventoryFilters.networkStatus = networkStatusGroups?.deactivated;
      }
      newHasFilters = true;
    }

    return { filters: simInventoryFilters, hasFilters: newHasFilters };
  }, [copiedStoredFilters, getNetworkStatusFromFriendly, networkStatusGroups, segmentName]);

  return {
    filters,
    hasUserFilterApplied: hasUserFiltersForSegment({ segmentName, filters: copiedStoredFilters }),
    filtersValidForAllSegment:
      !!filters?.organizationId || (filters?.iccidFilterProperty?.iccid?.length ?? 0) > 0,
    hasFilters,
    isLoading,
    error,
  };
};

export default useSimInventoryFilters;
